import React from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import _cx from 'classnames/bind'
import { Text, Table, Grid, Icon } from '@adc/ui-adc'
import { formatMoneyWithNumeral, formatNumber } from '../../utils/formatters'
import { buildRow, DataNotAvailable, privateView, CompCard } from './utils'
import cls from './index.scss'

const cx = _cx.bind(cls)

const elmId = 'rental_comps'
const FIELDS = [
  {
    label: 'Address',
    fieldPath: 'address.fulladdress',
  },
  {
    label: 'Rental Price',
    fieldPath: 'info.rentprice',
    format: date => formatMoneyWithNumeral(date, '-'),
  },
  {
    label: 'Rental Date',
    fieldPath: 'info.rentdate',
    format: rentDate =>
      moment(rentDate, 'YYYY-MM-DDTHH:mm:ss').format('MM/DD/YYYY'),
  },
  {
    label: 'Distance',
    fieldPath: 'distance',
    format: dist => (dist !== '-' ? `${dist} mi` : '-'),
  },
  {
    label: 'Bed',
    fieldPath: 'info.bedrooms',
  },
  {
    label: 'Bath',
    fieldPath: 'info.bathrooms',
  },
  {
    label: 'Sq. ft',
    fieldPath: 'info.livingarea',
    format: formatNumber,
  },
  {
    label: 'Lot Size',
    fieldPath: 'info.landarea',
    format: landarea =>
      landarea !== '-' ? `${(landarea / 43560).toFixed(2)} Acres` : '-',
  },
  {
    label: 'Built',
    fieldPath: 'info.yearbuilt',
    elmId: 'year_built',
  },
  {
    label: 'Source',
    fieldPath: 'source',
    format: source =>
      ['dataquick', 'Public Records'].includes(source)
        ? 'Public Records'
        : 'MLS',
  },
]

const PriceColumn = ({ price, label, icon, color }) => (
  <Grid.Column>
    <div
      className={cx(
        'c-flex',
        'u-display-f-vcenter',
        'u-display-f-hcenter',
        `circular-override-${color}`
      )}
    >
      <div>
        <Icon
          circular
          inverted
          name={icon}
          iconStyle='regular'
          color={color}
          size='big'
        />
      </div>
      <div>
        <Text size='tiny'>{label}</Text>
        <div>
          <Text size='massive' weight='heavy' color={color}>
            {formatMoneyWithNumeral(price, 'unknown')}
          </Text>
        </div>
      </div>
    </div>
  </Grid.Column>
)

PriceColumn.propTypes = {
  price: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
}

const showMap = rentals => rentals.length > 0
const showRentalRange = rentalRange => Object.values(rentalRange).some(Boolean)
const show = (rentals, rentalRange) =>
  showRentalRange(rentalRange) || showMap(rentals)

export const RentalRange = ({ rentalRange, rentals, zipcode }) => {
  const { low, high, median } = rentalRange || {}
  return show(rentals, rentalRange) ? (
    <>
      <Text size='large' weight='heavy'>
        Rental Estimates
      </Text>
      {showRentalRange(rentalRange) && (
        <>
          <Text className={cx('u-mt-4')} size='large'>
            Rental range for zip code {zipcode}
          </Text>
          <div className={cx('u-mt-6', 'u-mb-6')}>
            <Grid stackable columns={3} centered padded>
              <Grid.Row centered>
                <PriceColumn
                  price={high}
                  label='High Price'
                  icon='arrow-up'
                  color='success'
                />
                <PriceColumn
                  price={median}
                  label='Median Price'
                  icon='tilde'
                  color='navyBlue'
                />
                <PriceColumn
                  price={low}
                  label='Low Price'
                  icon='arrow-down'
                  color='caution'
                />
              </Grid.Row>
            </Grid>
          </div>
        </>
      )}
    </>
  ) : (
    <></>
  )
}

RentalRange.propTypes = {
  rentalRange: PropTypes.object.isRequired,
  rentals: PropTypes.array.isRequired,
  zipcode: PropTypes.string,
}

const RentalComps = ({
  rentals,
  loading,
  rentalRange,
  selectedProperty,
  selectPin,
  deSelectPin,
}) => {
  const selectedAsset = selectedProperty >= 0 && rentals[selectedProperty]

  if (loading) return null

  return !show(rentals, rentalRange) ? (
    <DataNotAvailable />
  ) : (
    showMap(rentals) && (
      <>
        <div className={cx('hide-on-small', 'u-mt-2')}>
          <Table selectable>
            <Table.Header>
              <Table.Row>
                {FIELDS.map(field => (
                  <Table.HeaderCell key={field.label}>
                    {field.label}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rentals.map((comp, i) =>
                buildRow({
                  comp,
                  key: i,
                  elmId,
                  fields: FIELDS,
                  active: selectedProperty === i,
                  onMouseEnter: selectPin(i),
                  onMouseLeave: deSelectPin(),
                })
              )}
            </Table.Body>
          </Table>
        </div>
        {selectedAsset && (
          <div className={cx('show-on-small')}>
            <CompCard comp={selectedAsset} fields={FIELDS} />
          </div>
        )}
      </>
    )
  )
}

RentalComps.propTypes = {
  rentals: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  selectedProperty: PropTypes.number.isRequired,
  selectPin: PropTypes.func.isRequired,
  deSelectPin: PropTypes.func.isRequired,
  rentalRange: PropTypes.object.isRequired,
}

export default privateView(RentalComps)
